import {ButtonBar, DataGridActionBar} from "@dain/mui-components";
import {SearchButton} from "../../common/button/CommonButtons";
import {useEffect, useRef, useState} from "react";
import {Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {SearchPanelState, useSubmissionSearch} from "../submission/useSubmissionSearch";
import {SubmissionDto, SubmissionFilterDto} from "../../client/submission/SubmissionApiClient";
import SubmissionGrid, {SubmissionColumn} from "../submission/SubmissionGrid";
import {PageLoadSize} from "../../common/pagination/PageableGrid";
import {SubmissionTranslKey} from "../submission/SubmissionTranslKey";
import {SortingOrderType} from "../../client/pagination/SortingFilterDto";
import {RecentSubmissionFilterForm} from "./RecentSubmissionFilterForm";

export const DEFAULT_SUBMISSION_FILTER: SubmissionFilterDto = {
    paging: {
        maxPageSize: PageLoadSize.INITIAL_PAGE_SIZE,
        pageToken: null,
    },
    sorting: {
        sortingCriteria: [
            { property: "submittedAt", order: SortingOrderType.ASC }
        ],
    },
};

export interface RecentSubmissionSearchPanelProps {
    actions?: React.ReactNode;
    specificDefaultFilter?: SubmissionFilterDto;
    submissionsGridDefColumns?: SubmissionColumn[];
    onSelectedSubmission: (submission?: SubmissionDto) => void;
    onSubmissionDoubleClick?: (submissionId: number) => void;
}

export default function RecentSubmissionSearchPanel(props: Readonly<RecentSubmissionSearchPanelProps>) {
    const {actions, specificDefaultFilter, submissionsGridDefColumns, onSelectedSubmission,
        onSubmissionDoubleClick} = props;

    const {t} = useTranslation();
    const isInitialized = useRef(false);

    const {
        submissionSearchPanelState,
        submissionFilter,
        submissionResults,
        displayedSubmissionsCount,
        fetchSubmissions,
        reloadDefaultSubmissionGrid,
        onFilterChanged,
        filterSearch,
        hasAnySubmissions
    } = useSubmissionSearch(specificDefaultFilter ?? DEFAULT_SUBMISSION_FILTER);

    const [selectedSubmission, setSelectedSubmission] = useState<SubmissionDto>();
    const [hasSearched, setHasSearched] = useState(false);

    function onSelectionChange(submission?: SubmissionDto) {
        setSelectedSubmission(submission);
        onSelectedSubmission(submission);
    }

    function onSearchClick() {
        setHasSearched(true);
        filterSearch();
    }

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            reloadDefaultSubmissionGrid();
        }
    }, [reloadDefaultSubmissionGrid]);

    return (
        <>
            {(hasAnySubmissions() || hasSearched) && (
                <>
                    <h4>{t(SubmissionTranslKey.RECENT_SUBMISSIONS_PANEL_TITLE)}</h4>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <RecentSubmissionFilterForm filter={submissionFilter} onChange={onFilterChanged} />
                        </Grid>
                        <Grid item xs={12}>
                            <ButtonBar>
                                <SearchButton onClick={onSearchClick} />
                            </ButtonBar>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                {actions && (
                                    <Grid item xs={12}>
                                        <DataGridActionBar actions={actions} />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    {submissionSearchPanelState !== SearchPanelState.INITIALIZED && (
                                        <SubmissionGrid
                                            rowsData={submissionResults.items}
                                            totalCount={displayedSubmissionsCount}
                                            nextPageToken={submissionResults.nextPageToken}
                                            submissionsGridDefColumns={submissionsGridDefColumns}
                                            submissionFilter={submissionFilter}
                                            fetchSubmissions={fetchSubmissions}
                                            selectedSubmissionId={selectedSubmission?.id}
                                            onSelectSubmission={onSelectionChange}
                                            onSubmissionDoubleClick={onSubmissionDoubleClick}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            )}
        </>
    );
};
